import * as React from 'react';
import PropTypes from 'prop-types';
import { Dialog as HeadlessDialog } from '@headlessui/react';
import { useLocation } from '@gatsbyjs/reach-router';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { useMutation } from 'react-query';
import { HiCheckCircle } from 'react-icons/hi';
import BaseDialog from 'components/BaseDialog';
import TextInput from 'components/TextInput';
import { useLocalStorage } from 'utils/frontend/storage-hooks';
import pint from 'images/first-order-pint.jpg';

const DELAY_MS = 20000;

const FirstOrderOffer = ({ active }) => {
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useLocalStorage('first_order_offer', false);
  const [ready, setReady] = React.useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm({ mode: 'onTouched' });

  const { mutate: submit, isLoading: submitting, isError, isSuccess } = useMutation(formData => (
    axios.post('/api/klaviyo/newsletter', formData)
  ));

  React.useEffect(() => {
    if (!active) return undefined;
    const timer = setTimeout(() => setReady(true), DELAY_MS);
    return () => clearTimeout(timer);
  }, [active]);

  const close = () => {
    if (!submitting) setDismissed(true);
  };

  return (
    <BaseDialog
      open={ready && !dismissed && pathname !== '/'}
      onClose={close}
      panelClassName="relative flex flex-col w-[min(100vw-2rem,26rem)] max-h-[92vh] overflow-y-auto rounded-2xl m-4 text-center bg-gradient-to-b from-white via-[#eefcf9] to-[#d2f5ef]"
    >
      <div className="flex justify-end px-2 pt-2">
        <button
          type="button"
          className="flex h-8 w-8 items-center justify-center rounded-full text-2xl leading-none text-zinc-900 hover:bg-white/70 focus:outline-none"
          onClick={close}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      <div className="px-6 pb-2">
        <HeadlessDialog.Title
          as="h2"
          className="mt-1 text-[1.45rem] xs:text-[1.6rem] font-black uppercase leading-[0.95] tracking-tight text-zinc-900"
        >
          <span className="block">The first round&apos;s on us</span>
          <span className="mt-1 block">(sort of)</span>
        </HeadlessDialog.Title>

        {isSuccess ? (
          <div className="mt-5 flex flex-col items-center gap-2 text-zinc-800">
            <HiCheckCircle className="text-4xl text-zinc-900" />
            <p className="font-medium">
              You&apos;re in. Check your inbox for 10% off your first order.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(submit)} className="mt-4 flex flex-col gap-3">
            <p className="mx-auto mb-1 max-w-[16rem] text-sm font-medium uppercase tracking-wide text-zinc-900">
              Enjoy 10% off your first order. We&apos;ll email you the code.
            </p>
            <TextInput
              placeholder="Email address"
              error={errors.email}
              disabled={submitting}
              type="email"
              {...register('email', {
                required: 'Add your email and we\'ll send the code',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'That email doesn\'t look right',
                }
              })}
            />
            <button
              type="submit"
              className="rounded-full bg-[#faaa00] px-6 py-3 text-sm font-black uppercase tracking-wide text-zinc-900 shadow-sm transition hover:brightness-95 active:scale-95 disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send my code'}
            </button>
            {isError && (
              <p className="text-sm font-medium text-gpred">
                Something went wrong. Try again in a minute.
              </p>
            )}
          </form>
        )}
      </div>

      <img
        src={pint}
        alt="Garage Project pint"
        className="mt-1 h-72 w-full object-cover object-[center_54%]"
      />
    </BaseDialog>
  );
};

FirstOrderOffer.propTypes = {
  active: PropTypes.bool.isRequired
};

export default FirstOrderOffer;
